/**
 * @author        grady@zumper.com (Grady Kuhnline), yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   ScrollToTop Component
 */

import { withRouter } from '@zcode/router-utils'
import type { RouteComponentProps } from '@zcode/router-utils'
import type { Location } from 'history'
import { Component } from 'react'
import { logger } from '../../../logger'

const callShouldScrollToTop = (
  shouldScrollToTop: ((location: Location, prevLocation?: Location) => boolean) | undefined,
  location: Location,
  prevLocation?: Location
) => {
  const sameLocation = location === prevLocation
  const toHashLocation = !!(location?.hash && !prevLocation?.hash)
  if (typeof shouldScrollToTop === 'function') {
    try {
      if (sameLocation || toHashLocation) {
        return false
      }
      return shouldScrollToTop(location, prevLocation)
    } catch (error) {
      logger.error('ScrollToTop: shouldScrollToTop prop threw', error)
    }
  }
  return !sameLocation && !toHashLocation
}

interface Props extends RouteComponentProps {
  ignoreHashChange?: boolean
  ignoreLocationChange?: boolean
  ignorePathnameChange?: boolean
  ignoreSearchChange?: boolean
  shouldScrollToTop?: (location: Location, prevLocation?: Location) => boolean
  skipMount?: boolean
}

// https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/docs/guides/scroll-restoration.md
class ScrollToTop extends Component<Props> {
  componentDidMount() {
    const { location, shouldScrollToTop, skipMount } = this.props

    const shouldSkipScroll = !callShouldScrollToTop(shouldScrollToTop, location)
    // prevent scrolling to the top if the location is mounted w/ hash
    if (skipMount || shouldSkipScroll || !!location?.hash) {
      return
    }
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0)
    }
  }

  componentDidUpdate(prevProps: Props) {
    const {
      ignoreHashChange,
      ignoreLocationChange,
      ignorePathnameChange,
      ignoreSearchChange,
      location,
      shouldScrollToTop,
    } = this.props

    const shouldScroll = callShouldScrollToTop(shouldScrollToTop, location, prevProps.location)
    if (!ignoreLocationChange && shouldScroll) {
      const { hash, pathname, search } = location || {}
      const pathnameChanged = !ignorePathnameChange && prevProps.location?.pathname !== pathname
      const searchChanged = !ignoreSearchChange && prevProps.location?.search !== search
      const hashChanged = !ignoreHashChange && prevProps.location?.hash !== hash
      const nothingChanged = !pathnameChanged && !searchChanged && !hashChanged
      if (nothingChanged) {
        return
      }
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0)
      }
    }
  }

  render() {
    return null
  }
}

export default withRouter(ScrollToTop)
